import React, { useContext, useEffect, useLayoutEffect, useRef, useState} from "react"
import { CardContent,Card, CardFooter, CardHeader } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { AppDataContext } from "modules/main/context/appData"
import { Amount } from "utils/amount"
import { ProductData, StoreDetails, PromoStr } from "modules/product/types/product"
import { CircleArrowDown, CircleArrowUp,  RefreshCw, ShoppingBasket, ShoppingCart, SquarePlus } from "lucide-react"
import { Payload, ProductSearchSelectionHandler, ValidationConflict } from "modules/cart/types/cart"
import { getLogoMembership, getStoreBdData} from "utils/storeDataManager"
import { AddDeleteCartButtons } from "../../cart/components/cartElements"

import { Link, useLocation } from "react-router-dom"


import { AddCartMethod } from "modules/cart/types/add-cart-methods"

import { cn } from "@/lib/utils"
import { Avatar, AvatarImage } from "@/components/ui/avatar"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { StoreData } from "modules/main/types/storeData"
import { Separator } from "@/components/ui/separator"
import { useAuth } from "modules/authentication/hooks/use-auth"
import posthog from "posthog-js"
import { getVisitorId } from "utils/analytics"

const sendEventPH = () => {
    posthog.capture('Añadir al carrito')
}

const getProductCategoryId = (product: ProductData): number => {
    return product.category_id;
}


export const ProductCard:React.FC<{product:ProductData, 
                    addCallback:ProductSearchSelectionHandler, 
                    recalculateCallBack:(payload:Payload) => void,
                    validatingCallBack:(payload:Payload) => Promise<ValidationConflict>,
                    inCart:number,
                    isDesktop:boolean,
                    attribution_token?:string | null
                    }> = (
    {
        product,
        addCallback,
        recalculateCallBack,
        validatingCallBack,
        inCart,
        isDesktop,
        attribution_token,
        ...props
    }) => {

        
    const [addLoading, setAddLoading] = useState(false);
    const [qty, setQty] = useState(inCart);
    const qtyRef = useRef(qty);    
    const activeControllerRef = useRef<AbortController | null>(null);
    const {isLogged} = useAuth();
    useEffect(() => {
        return () => {
            if (activeControllerRef.current) {
                activeControllerRef.current.abort();
                activeControllerRef.current = null;
            }
        };
    }, []);
    
    useEffect(() => { 
        qtyRef.current = qty;
        if(qty === 0)
            setAddLoading(false);
    
    }, [qty]);

    
    const bestIndex = product.stores.findIndex((store) => store.sku === product.price_detail.best_price_id);
    const validBestIndex = bestIndex  <= product.stores.length -1 ? bestIndex : 0;
    const [selectedIndex,setIndex] = useState(validBestIndex);
    

    useLayoutEffect(() => {
        if (selectedIndex !== validBestIndex) {
            setIndex(validBestIndex);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validBestIndex]);


    const worstIndex = product.stores.findIndex((store) => store.sku === product.price_detail.worst_price_id);
    const worstPrice = product.stores[worstIndex];
    const bestPrice = product.stores[bestIndex];


    const selectedStore = product.stores[selectedIndex]??product.stores[0];
    const selectedPrice = selectedStore.price;
    
    /**
     * Canonical hashed SKU captured from the ProductData prop (outer closure).
     * hashed_sku is a catalog-level identifier that is consistent across all stores
     * for the same product. It lives on ProductData, not on StoreDetails.
     * Used in handleAdd to populate Payload.hashedSku for the picker linking flow.
     * Risk: if the API omits hashed_sku (older responses), this is null — safe,
     * because handlePickerProductSelected falls back to payload.sku in that case.
     */
    const catalogHashedSku = product.hashed_sku ?? null;

    const {storeList} = useContext(AppDataContext);

    
    
    const priceDifference = (selectedIndex === bestIndex)? 
                                (product.price_detail.best_price - worstPrice.price):
                                (selectedPrice - bestPrice.price);
    
    const discountPercent = (selectedIndex === bestIndex)? 
                            (priceDifference/worstPrice.price):
                            (priceDifference/bestPrice.price);
    
    const handleAdd = async (method:AddCartMethod,product_id:string,selectedStoreData:StoreDetails,category_id:number) => {
        setAddLoading(true);
        // setSkuAdded(product.sku);
        window.dataLayer.push({
            'event': 'add_to_cart',       
            'product_id': selectedStoreData.s_id,   
            'quantity': 1,                
            'visitor_id': getVisitorId(),
            'attribution_token': attribution_token 
        });

        const payload: Payload = {
                id:product_id,
                sku: selectedStoreData.sku,    
                name:selectedStoreData.name,  
                category_id,
                quantity:inCart + 1,
                method:"SAME", //FIXME
                /**
                 * hashed_sku is read from the outer ProductData prop (closure), not from the
                 * StoreDetails argument. hashed_sku is a catalog-level identifier — it is
                 * consistent across all stores for the same product, but lives on ProductData,
                 * not StoreDetails. The first store (stores[0]) is used as a canonical fallback
                 * only in other contexts; here we read directly from the ProductData prop.
                 * Risk: if the API omits hashed_sku, this falls back to null (safe — callers
                 * in handlePickerProductSelected will then use payload.sku as fallback).
                 */
                hashedSku: catalogHashedSku,
                s_id: selectedStoreData.s_id,
            };

        addCallback(payload, {
            product: product,
            selectedStore: selectedStoreData,
        });     
        validatingCallBack ({
            id: product_id,
            sku: selectedStoreData.sku,
            name: selectedStoreData.name,
            category_id,
            quantity: 1,
            method: "SAME", // FIXME
            hashedSku: catalogHashedSku,
        })      
    }

    const handleAddClick = (method:AddCartMethod, product_id:string, selectedStoreData:StoreDetails, setQuantity:(qty:number)=>void) => {
        sendEventPH();
        setQuantity(1);
        handleAdd(method, product_id, selectedStoreData, getProductCategoryId(product));
    }

    
    return (
    <>
    {(!isDesktop)?
    <ProductCardMobile
        discountPercent={discountPercent}
        priceDifference={priceDifference}
        product={product}
        selectedStore={selectedStore}
        storeList={storeList}
        inCart={inCart}
        qty={qty}
        setQty={setQty}
        selectedPrice={selectedPrice}
        // disableAdd={disableAdd}
        handleAddClick={handleAddClick}
        addLoading={addLoading}
        addCallback={addCallback} 
        loggedIn={isLogged}
        />
    :
    <ProductCardDesktop 
        discountPercent={discountPercent}
        priceDifference={priceDifference}
        product={product}
        selectedStore={selectedStore}
        storeList={storeList}
        inCart={inCart}
        qty={qty}
        setQty={setQty}
        selectedPrice={selectedPrice}
        // disableAdd={disableAdd}
        handleAddClick={handleAddClick}
        addLoading={addLoading}
        addCallback={addCallback} 
        loggedIn={isLogged}
        />}
    </>
);
}

interface ProductCardProps{
    inCart:number,
    qty:number,
    discountPercent:number,
    priceDifference:number,
    product:ProductData,
    selectedStore:StoreDetails,
    selectedPrice:number,
    storeList: StoreData[];
    // disableAdd:boolean;
    addLoading:boolean;
    handleAddClick:(method:AddCartMethod,product_id:string,product:StoreDetails,setQuantity:(qty:number)=>void) => void;
    addCallback:ProductSearchSelectionHandler;
    setQty:(qty:number)=>void;
    loggedIn:boolean;
}


const ProductCardDesktop:React.FC<ProductCardProps> = ({discountPercent,product,selectedStore,storeList,priceDifference,inCart,selectedPrice,addLoading,...props}) => {
    const imageUrl = selectedStore.images?.catalog??selectedStore.image_url;
    const location = useLocation();
    return (
    <Card className="max-w-[18rem] 2xl:max-w-80  min-w-[18rem] 2xl:min-w-80 flex flex-col gap-0 pt-0">
        <CardHeader className="p-0">
            <div className={cn("text-xs rounded-br-xl text-center text-white font-bold px-2 py-1 max-w-14"
                                ,discountPercent<0?"best-price-label":"background-color")}>
                <Amount 
                    amount={discountPercent} 
                    currency="CLP" 
                    locale="es-CL" 
                    formatStyle="percent"
                />                
            </div>
        </CardHeader>
        <CardContent className="pb-4">
            <div className="flex flex-col p-0  w-full gap-2">
                <div className="relative w-full">
                    <div className="mx-auto w-32">
                        <Link to={"/"+product.slug} state={{ from: location.pathname }}>
                            <img className="Image-120" src={imageUrl} alt={""}/>
                        </Link>
                    </div>
                    <div className="absolute bottom-0 right-0">
                        {/* <Badge variant="outline">{product.total} {product.base_unit}</Badge> */}
                    </div>
                </div>                
                <div  className="min-h-14 min-w-0">
                    <h4 className="scroll-m-20 text-sm font-semibold tracking-tight break-words"><Link to={"/"+product.slug} state={{ from: location.pathname }}>{selectedStore?.name}</Link></h4>                    
                    <small className="text-xs font-medium text-muted-foreground leading-none">{product.brand}</small>                    
                </div>
                <div className="flex gap-2 items-center flex-wrap min-h-8">
                    { (discountPercent !== 0) &&
                    (<>
                    <Badge variant={(discountPercent<0)?"default":"destructive"}>{
                        (discountPercent<0)?
                            <CircleArrowDown className="mr-2 h-4 w-4 shrink-0 opacity-80"/>:
                            <CircleArrowUp className="mr-2 h-4 w-4 shrink-0 opacity-80"/>}
                        {(discountPercent < 0)?
                            (<span className="mr-2">¡Ahorras </span>):
                            (<span>¡</span>)}
                        <Amount 
                            amount={Math.abs(priceDifference)} 
                            currency="CLP" 
                            locale="es-CL" 
                        />
                        {(discountPercent > 0)?
                            (<span className="ml-2">Más Caro!</span>):
                            (<span>!</span>)}                        
                    </Badge>                      
                    {(discountPercent<0 && (selectedPrice >product.price_detail.best_price ))&&
                    (<Badge>Oferta</Badge>)}                                
                    </>)
                    }
                </div>
                <div className="flex w-full p-0 gap-2">
                    <StoreDetail product={product} selectedStore={selectedStore} storeList={storeList}/>
                </div>  
                {
                (product.stores.length > 1)&&
                (
                <div className="flex flex-col w-full">
                    <Accordion type="single" collapsible>
                        <AccordionItem value={selectedStore.store_id.toString()} className="border-hidden">
                            <AccordionTrigger className="flex w-full p-0 gap-2 text-xs font-semibold text-muted-foreground">
                                <div className="flex flex-col w-full items-start gap-1">
                                    <div className="flex flex-row gap-2 items-center">
                                        <span>Otras Alternativas: </span>
                                        <div className="flex flex-row gap-1">
                                        {
                                            product.stores.map((store,index) => {
                                                if(store.sku === selectedStore.sku) return <React.Fragment key={index}></React.Fragment>;
                                                return(
                                                    <Avatar key={index} className="w-5 h-5">
                                                        <AvatarImage src={getStoreBdData(Number(store.store_id), storeList).logo_url} />
                                                    </Avatar>
                                                );
                                            })
                                        }                             
                                        </div>                                      
                                    </div>                                                                        
                                    <Separator />                                    
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="pb-0 pt-2">                                                                                         
                                <div className="flex flex-col gap-2 w-full pr-2">
                                {
                                product.stores.map((store,index) => {
                                    if(store.sku === selectedStore.sku) return <React.Fragment key={index}></React.Fragment>;
                                    return(
                                        <React.Fragment key={index}>
                                            <StoreDetail  product={product} selectedStore={store} storeList={storeList}/>
                                        </React.Fragment>
                                    );
                                    })
                                }
                                </div>                                                            
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
                )   
                }                
            </div>                            
        </CardContent>
        <CardFooter className="justify-center mt-auto">
        {                       
        (inCart >0)?
            (<AddDeleteCartButtons 
                className="w-full"
                product_id={product.id}
                sku={selectedStore.sku}
                name={selectedStore.name}
                category_id={getProductCategoryId(product)}
                quantity={inCart}
                setQty={props.setQty}
                method="SAME"
                showDelete={false}
                // disabled={props.disableAdd}
                PlusMinusCallback={props.addCallback}
            />):
            
                (!addLoading)?
                <Button data-ph-event="add-to-cart" className="text-sm min-w-[6.8rem]" onClick={ 
                    () => {
                        props.handleAddClick("SAME",product.id,selectedStore,props.setQty)}}><ShoppingCart className="mr-2 h-3 w-3" /><span>Añadir</span></Button>:                
                    <Button className="text-sm w-[6.8rem]" disabled={true} onClick={ () => {}}><RefreshCw className="h-6 w-6 animate-spin text-white" /></Button>                                                                       
        }
        </CardFooter>        
    </Card>        
    );
}


const ProductCardMobile:React.FC<ProductCardProps> = ({discountPercent,product,selectedStore,storeList,priceDifference,inCart,selectedPrice,addLoading,...props}) => {
    const imageUrl = selectedStore.images?.catalog??selectedStore.image_url;
    const location = useLocation();
    return (
        <div className="flex flex-col gap-2 w-full">
            <div className="flex gap-2 w-full">
                <div className="flex flex-col gap-2 my-auto">
                    {
                    (discountPercent !== 0) &&(
                    <div className={cn("top-0 text-xs rounded-br-xl text-center text-white font-bold px-2 py-1 max-w-14"
                            ,discountPercent<0?"best-price-label":"background-color")}>
                        <Amount 
                        amount={discountPercent} 
                        currency="CLP" 
                        locale="es-CL" 
                        formatStyle="percent"
                        /> 
                    </div>
                    )
                    }
                    <div className=" min-w-24">
                        <Link to={"/"+product.slug} state={{ from: location.pathname }}>
                            <img className="Image-80" src={imageUrl} alt={""}/>
                        </Link>
                    </div>
                    <div>
                        {(priceDifference < 0)&&
                        <Badge variant={(discountPercent<0)?"default":"destructive"}>{
                            (discountPercent<0)?
                                <CircleArrowDown className="mr-2 h-4 w-4 shrink-0 opacity-80"/>:
                                <CircleArrowUp className="mr-2 h-4 w-4 shrink-0 opacity-80"/>}
                            <Amount 
                                amount={Math.abs(priceDifference)} 
                                currency="CLP" 
                                locale="es-CL" 
                            />                     
                        </Badge>  } 
                    </div>
                </div>
                <div className="flex flex-1 flex-col w-full gap-1 min-w-0 max-w-full overflow-hidden">
                    <h4 className="scroll-m-20 text-xs font-semibold tracking-tight min-w-0 max-w-full overflow-hidden">
                        <Link to={"/"+product.slug} state={{ from: location.pathname }} className="block max-w-full whitespace-normal">{selectedStore?.name}</Link>
                    </h4>                    
                    <small className="text-xxs font-medium text-muted-foreground leading-none mb-1">{product.brand}</small>
                    <StoreDetail product={product} selectedStore={selectedStore} storeList={storeList} mobile={true}/>
                    {
                    product.stores.length > 1 && (
                    <div className="flex flex-col my-2">
                    <Accordion type="single" collapsible>
                        <AccordionItem value={selectedStore.store_id.toString()} className="border-hidden">
                        <AccordionTrigger className="flex w-full p-0 gap-2 text-xs font-semibold text-muted-foreground">
                                <div className="flex flex-col w-full items-start gap-1">
                                    <div className="flex flex-row gap-2 items-center">
                                        <span>Otras Alternativas: </span>
                                        <div className="flex flex-row gap-1">
                                        {
                                            product.stores.map((store,index) => {
                                                if(store.sku === selectedStore.sku) return <React.Fragment key={index}></React.Fragment>;
                                                return(
                                                    <Avatar key={index} className="w-5 h-5">
                                                        <AvatarImage src={getStoreBdData(Number(store.store_id), storeList).logo_url} />
                                                    </Avatar>
                                                );
                                            })
                                        }                             
                                        </div>                                      
                                    </div>                                                                        
                                    <Separator />                                    
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="pb-0 pt-2">                                                                                         
                                <div className="flex flex-col gap-2 w-full pr-0">
                                {
                                product.stores.map((store,index) => {
                                    if(store.sku === selectedStore.sku) return <React.Fragment key={index}></React.Fragment>;
                                    return(
                                        <React.Fragment key={index}>
                                            <StoreDetail  product={product} selectedStore={store} storeList={storeList} mobile={true}/>
                                        </React.Fragment>
                                    );
                                    })
                                }
                                </div>                                                            
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>

                    </div>
                    )
                    }                    
                </div>
            </div>
            <div className="ml-auto">
            {
            (inCart >0)?
            (<AddDeleteCartButtons 
                className="w-full"
                product_id={product.id}
                category_id={getProductCategoryId(product)}
                sku={selectedStore.sku}
                name={selectedStore.name}
                quantity={inCart}
                method="SAME"
                showDelete={false}
                setQty={props.setQty}
                // disabled={props.disableAdd}
                PlusMinusCallback={props.addCallback}
            />):        
                (!addLoading)?
                    <Button className="text-sm min-w-[6.8rem]" onClick={ 
                        () => {
                            props.handleAddClick("SAME",product.id,selectedStore,props.setQty)}}>
                                <SquarePlus className="mr-2 h-3 w-3" /><span>Añadir</span></Button>:
                        <Button className="text-sm w-[6.8rem]" disabled={true} onClick={ () => {}}><RefreshCw className="h-6 w-6 animate-spin text-white" /></Button>                                         
            } 
            </div>
            <Separator className="w-full my-2" />
        </div> 
    );
}



export const UNRESTRICTED = ["", "internet_only"] as const;
type UnrestrictedRestriction = (typeof UNRESTRICTED)[number];

const isUnrestrictedRestriction = (
  restriction: string,
): restriction is UnrestrictedRestriction => {
  return restriction === "" || restriction === "internet_only";
};

/**
 * Selects the active promo for a store using two-pass logic:
 * 1. Prefer a regular promo with apply===true and unrestricted restriction
 * 2. Fall back to the first unrestricted regular promo
 * Returns undefined if no eligible promo exists (caller falls back to base price).
 */
export const selStorePromoPrice = (
  promos: StoreDetails["promos"],
): PromoStr | undefined => {
  const applied = promos.find(
    (p) =>
      p.type === "regular" &&
      p.apply === true,
  );
  if (applied) return applied;

  const unrestricted = promos.find(
    (p) =>
      p.type === "regular" &&
      isUnrestrictedRestriction(p.restrictions[0] ?? ""),
  );
  return unrestricted;
};

interface StoreDetailProps{
    product:ProductData,
    selectedStore:StoreDetails,
    storeList: StoreData[];
    mobile?:boolean
}

const StoreDetail:React.FC<StoreDetailProps> = ({
                                                product
                                                ,selectedStore
                                                ,storeList
                                                ,...props}) => {
                                                     
    const activePromo = selStorePromoPrice(selectedStore.promos);
    const activePromoRestriction = activePromo?.restrictions?.[0] ?? "";
    const showInlineAppliedPromoBadge =
        activePromo?.type === "regular" &&
        activePromo?.apply === true &&
        activePromoRestriction !== "";
    const isBestPrice = selectedStore.sku === product.price_detail.best_price_id;
    const noSelected = selectedStore.sku !== product.price_detail.best_price_id;
    const pricePerUnit = activePromo ? activePromo.price_per_unit : selectedStore.price_per_unit;

    return(
    <div className={cn("flex flex-col items-center gap-1",{
        "flex-1 justify-center":!props.mobile,
    })}>
        <div className="flex flex-row gap-6 w-full justify-between">
            <div className="flex flex-row gap-2 items-center">          
                <a href={selectedStore?.url} target="_blank" rel="noopener noreferrer" title="Ir a la página del producto">                          
                <Avatar className={
                    cn("",{
                        "w-6 h-6":noSelected && !props.mobile,
                        "w-8 h-8":!noSelected && !props.mobile,
                        "w-4 h-4":noSelected && props.mobile,
                        "w-5 h-5":!noSelected && props.mobile
                    })
                }>
                    <AvatarImage src={getStoreBdData(Number(selectedStore.store_id), storeList).logo_url}/>
                </Avatar>
                </a>
                <span className="text-muted-foreground text-xs">{getStoreBdData(Number(selectedStore.store_id), storeList).name}</span>
            </div>
            <div className={cn("line-through text-muted-foreground my-auto",
                {"text-xs":props.mobile}
            )}>                                        
                {
                (activePromo && selectedStore.price !== activePromo.price ) &&
                <Amount
                    amount={selectedStore.price}
                    currency="CLP" 
                    locale="es-CL" 
                    />        }                             
            </div>
            <div className={cn("my-auto flex flex-col gap-0 text-xs",
                {
                "best-price font-bold text-base": selectedStore.sku === product.price_detail.best_price_id && product.price_detail.best_price !== product.price_detail.worst_price,
                "font-bold text-base": selectedStore.sku === product.price_detail.best_price_id,
                "text-muted-foreground": selectedStore.sku !== product.price_detail.best_price_id,
                "text-sm":props.mobile
                }
            )} >
            {(activePromo)?
                (
                <>
                <div className="flex flex-row items-center gap-2">
                    {showInlineAppliedPromoBadge && (
                        <Badge variant="secondary" className="text-muted-foreground text-xxs px-1.5 py-0.5">
                            <img
                                src={getLogoMembership(storeList,selectedStore.store_id,activePromoRestriction)}
                                alt={activePromoRestriction}
                            />
                        </Badge>
                    )}                    
                    <Amount
                        amount={activePromo?.price??selectedStore.price}
                        currency="CLP" 
                        locale="es-CL" 
                    />
                </div>
                </>                           
                ):
                (
                    
                <Amount
                    amount={selectedStore.price}
                    currency="CLP" 
                    locale="es-CL" 
                    />                                             
                )
                
            }                                        
            </div>
        </div>
        {
        (selectedStore.show_pper_unit && isBestPrice)&&
        <div className="text-secondary ml-auto">
                <Badge className={cn("flex gap-1 items-center text-muted-foreground",{
                    "bg-green-50": selectedStore.sku === product.price_detail.best_price_id && product.price_detail.best_price !== product.price_detail.worst_price
                })} variant="secondary">
                    <ShoppingBasket className="mr-1"/>
                    <Amount
                        amount={pricePerUnit}
                        currency="CLP" 
                        locale="es-CL"                                     
                    />
                    <span> x {selectedStore.price_per_unit_scale} {product.unit}</span>
                </Badge>
        </div>
        }
        {

        (selectedStore.promos.length > 1 ||     
            ( selectedStore.promos.length === 1 && 
                (selectedStore.promos[0].restrictions[0] === "" || selectedStore.promos[0].restrictions[0] === "internet_only" )))
        && (
            <div className="flex flex-row w-full gap-2">
            {
                selectedStore.promos.map((promo,index) => {                   

                    if((promo.type === "regular" && promo.apply === true) || promo.restrictions.length > 1)
                        return <React.Fragment key={index}></React.Fragment>;
                    return (
                    <Badge key={index} variant="secondary" className="text-muted-foreground text-xxs">
                        <div className="flex flex-row gap-1 items-center justify-start">
                        {
                        (promo.restrictions[0] !== "internet_only" && promo.restrictions.length === 1)&&
                            <>
                            <img src={getLogoMembership(storeList,selectedStore.store_id,promo.restrictions[0])} alt={promo.restrictions[0]}/>
                                <Amount
                                    amount={promo.price}
                                    currency="CLP" 
                                    locale="es-CL" 
                                    /> 
                            </>
                            
                        }                        
                        <span>
                        {
                        (promo.type !== "regular")&&
                        (<>Llevas {promo.qty_affected} por <Amount
                            amount={promo.price}
                            currency="CLP" 
                            locale="es-CL" 
                            /> </>)
                        }
                        </span>
                        </div>
                    </Badge>
                )})
            }
            </div>            
        )
        }
        
    </div>
    )

}
